// Service-scoped disk attachments table + service-originated "Attach Disk"
// entry point. Reads the org-wide attachments via fetchDiskAttachments and
// filters to the target service with selectDiskAttachmentsByServiceId.
import {
  estimateMonthlyCost,
  fetchDiskAttachments,
  fetchEnvironmentById,
  fetchPersistentDisks,
  selectDiskAttachmentsByServiceId,
  selectEnvironmentById,
  selectPersistentDisksAsList,
  selectServiceById,
} from "@app/deploy";
import { useQuery, useSelector } from "@app/react";
import { schema } from "@app/schema";
import { capitalize } from "@app/string-utils";
import type { DeployDiskAttachment, DeployPersistentDisk } from "@app/types";
import { Button } from "@app/ui/shared/button";
import { costColDef } from "@app/ui/shared/cost-estimate-tooltip";
import {
  CurrencyCell,
  DataTable,
  DataTableCell,
  type DataTableCellRendererProps,
  type DataTableColDef,
  DateTimeCell,
  idColDef,
} from "@app/ui/shared/datatable";
import { DropdownMenu } from "@app/ui/shared/dropdown-menu";
import { Group } from "@app/ui/shared/group";
import {
  IconCylinder,
  IconEllipsisHorizontal,
  IconPlusCircle,
} from "@app/ui/shared/icons";
import { useMemo, useState } from "react";
import { AttachDiskToServiceModal } from "./attach-disk-to-service-modal";
import { DetachPersistentDiskModal } from "./detach-persistent-disk-modal";
import { ResizePersistentDiskModal } from "./resize-persistent-disk-modal";

interface AttachmentRow {
  attachment: DeployDiskAttachment;
  disk: DeployPersistentDisk | undefined;
  serviceHandle: string;
  envHandle: string;
}

// Detach target for a service-scoped attachment row.
interface DetachTarget {
  disk: DeployPersistentDisk;
  attachment: DeployDiskAttachment;
  serviceHandle: string;
}

// Renders the cost for a single attached disk. "-" when the disk record is
// missing (e.g., not yet hydrated). Pulled out so the cost-rates selector can
// be read via a hook from inside the cell renderer.
const AttachmentCostCell = ({
  disk,
}: {
  disk: DeployPersistentDisk | undefined;
}) => {
  const rates = useSelector(schema.costRates.select);
  if (!disk) return <span className="text-gray-400">-</span>;
  const cost = estimateMonthlyCost({
    rates,
    persistentDisks: [{ size: disk.size, provisionedIops: 0 }],
  });
  return <CurrencyCell value={cost} />;
};

export const ServiceDiskAttachmentsList = ({
  serviceId,
}: {
  serviceId: string;
}) => {
  useQuery(fetchPersistentDisks());
  useQuery(fetchDiskAttachments());

  const [isAttachOpen, setIsAttachOpen] = useState(false);
  const [detachTarget, setDetachTarget] = useState<DetachTarget | null>(null);
  const [resizeDisk, setResizeDisk] = useState<DeployPersistentDisk | null>(
    null,
  );

  const service = useSelector((s) => selectServiceById(s, { id: serviceId }));
  useQuery(fetchEnvironmentById({ id: service.environmentId }));
  const environment = useSelector((s) =>
    selectEnvironmentById(s, { id: service.environmentId }),
  );
  const attachments = useSelector((s) =>
    selectDiskAttachmentsByServiceId(s, { serviceId }),
  );
  const disks = useSelector(selectPersistentDisksAsList);

  const rows = useMemo<AttachmentRow[]>(() => {
    const diskById = new Map(disks.map((d) => [d.id, d]));
    return attachments
      .filter((a) => a.status !== "deprovisioned")
      .map((a) => ({
        attachment: a,
        disk: diskById.get(a.persistentDiskId),
        serviceHandle: service.handle,
        envHandle: environment.handle,
      }));
  }, [attachments, disks, service.handle, environment.handle]);

  // Column order: Handle (with disk icon), ID, Environment, Mount Path,
  // Disk Size, Status, Created, Cost/mo, Actions.
  const colDefs = useMemo<DataTableColDef<AttachmentRow>[]>(
    () => [
      {
        // Renderer-only columns reference nested data (attachment/disk), so
        // there's no flat row key to bind via `field` — use `colId` instead.
        colId: "handle",
        headerName: "Handle",
        cellRenderer: ({ data }: DataTableCellRendererProps<AttachmentRow>) => (
          <Group variant="horizontal" size="sm" className="items-center">
            <IconCylinder variant="sm" className="text-gray-500 shrink-0" />
            <DataTableCell
              label={data.disk?.handle ?? data.attachment.persistentDiskId}
            />
          </Group>
        ),
      },
      {
        colId: "id",
        ...idColDef,
        cellRenderer: ({ data }: DataTableCellRendererProps<AttachmentRow>) => (
          <DataTableCell
            label={data.disk?.id ?? data.attachment.persistentDiskId}
          />
        ),
      },
      {
        colId: "environment",
        headerName: "Environment",
        cellRenderer: ({ data }: DataTableCellRendererProps<AttachmentRow>) => (
          <DataTableCell label={data.envHandle} />
        ),
      },
      {
        colId: "mountPath",
        headerName: "Mount Path",
        cellRenderer: ({ data }: DataTableCellRendererProps<AttachmentRow>) => (
          <DataTableCell label={data.attachment.mountPoint} />
        ),
      },
      {
        colId: "size",
        headerName: "Disk Size",
        cellRenderer: ({ data }: DataTableCellRendererProps<AttachmentRow>) => (
          <DataTableCell label={data.disk ? `${data.disk.size} GB` : "-"} />
        ),
      },
      {
        colId: "status",
        headerName: "Status",
        cellRenderer: ({ data }: DataTableCellRendererProps<AttachmentRow>) => (
          <DataTableCell label={capitalize(data.attachment.status)} />
        ),
      },
      {
        colId: "createdAt",
        headerName: "Created",
        cellRenderer: ({ data }: DataTableCellRendererProps<AttachmentRow>) => (
          <DateTimeCell date={data.attachment.createdAt} />
        ),
      },
      {
        ...costColDef,
        colId: "cost",
        cellRenderer: ({ data }: DataTableCellRendererProps<AttachmentRow>) => (
          <AttachmentCostCell disk={data.disk} />
        ),
      },
      {
        colId: "actions",
        headerName: "Actions",
        sortable: false,
        cellRenderer: ({ data }: DataTableCellRendererProps<AttachmentRow>) => (
          <DropdownMenu
            label={<IconEllipsisHorizontal variant="sm" />}
            items={[
              {
                id: "detach",
                label: "Detach",
                // Detach acts on this attachment. Disabled when the underlying
                // disk record isn't available (the detach modal needs it).
                disabled: !data.disk,
                onClick: (close) => {
                  if (data.disk) {
                    setDetachTarget({
                      disk: data.disk,
                      attachment: data.attachment,
                      serviceHandle: data.serviceHandle,
                    });
                  }
                  close();
                },
              },
              {
                id: "resize",
                label: "Resize",
                onClick: (close) => {
                  if (data.disk) {
                    setResizeDisk(data.disk);
                  }
                  close();
                },
              },
            ]}
          />
        ),
      },
    ],
    [],
  );

  return (
    <Group>
      <div className="flex justify-end">
        <Button onClick={() => setIsAttachOpen(true)}>
          <IconPlusCircle variant="sm" />
          <div className="ml-2">Attach Disk</div>
        </Button>
      </div>

      <DataTable<AttachmentRow> rowData={rows} colDefs={colDefs} />

      <AttachDiskToServiceModal
        isOpen={isAttachOpen}
        service={service}
        onClose={() => setIsAttachOpen(false)}
      />

      {detachTarget ? (
        <DetachPersistentDiskModal
          isOpen={detachTarget !== null}
          disk={detachTarget.disk}
          attachment={detachTarget.attachment}
          serviceHandle={detachTarget.serviceHandle}
          onClose={() => setDetachTarget(null)}
        />
      ) : null}

      {resizeDisk ? (
        <ResizePersistentDiskModal
          isOpen={resizeDisk !== null}
          disk={resizeDisk}
          onClose={() => setResizeDisk(null)}
        />
      ) : null}
    </Group>
  );
};
